/**
 * KISSED University · About page.
 * Route: /university/about
 *
 * DRAFT COPY — 2026-07-06. Placeholder-quality mission/faculty content
 * pending final review by Dr. Christy Vonta / admissions leadership.
 */
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sparkles, Leaf, FlaskConical, BookOpen, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import UniversityTopNav from "@/components/UniversityTopNav";

const COLLEGES = [
  { key: "trichology", name: "College of Trichology Science", icon: Sparkles, to: "/university/college/trichology" },
  { key: "herbal",     name: "College of Herbal Medicine",    icon: Leaf,     to: "/university/college/herbal" },
  { key: "hhfw",       name: "Holistic Health & Functional Wellness", icon: FlaskConical, to: "/university/college/hhfw" },
  { key: "ce",         name: "Professional & Continuing Education", icon: BookOpen, to: "/university/ce-calendar" },
];

export default function UniversityAbout() {
  return (
    <div data-testid="university-about" className="min-h-screen bg-gradient-to-b from-[#faf7f5] via-white to-[#f5f0ec]">
      <UniversityTopNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/university" className="text-xs text-slate-600 inline-flex items-center hover:text-purple-900" data-testid="about-back-link">
          <ChevronLeft className="w-3 h-3 mr-1" /> Back to KISSED University
        </Link>

        {/* Draft-copy notice */}
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-50 border border-amber-200 px-3 py-1 text-[11px] text-amber-900" data-testid="draft-copy-flag">
          Draft copy — pending final review
        </div>

        {/* Hero */}
        <div className="mt-6">
          <span className="text-xs uppercase tracking-[0.18em] text-purple-900 font-semibold">About</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight" data-testid="about-title">
            KISSED University of<br />Healing Arts &amp; Sciences™
          </h1>
          <p className="mt-6 text-lg text-slate-700 leading-relaxed">
            KISSED University (KUHAS) is where clinical rigor meets sacred work. Founded by
            Dr.&nbsp;Christy&nbsp;Vonta, KUHAS trains the next generation of trichologists,
            herbalists, and holistic health practitioners through a fully articulated
            credential pathway — from foundational certification through doctoral scholarship.
          </p>
        </div>

        {/* Mission */}
        <section className="mt-12" data-testid="about-mission">
          <h2 className="font-display text-2xl text-slate-900">Our Mission</h2>
          <p className="mt-3 text-base text-slate-700 leading-relaxed">
            To equip practitioners with the science, ethics, and consent-first client care
            required to serve individuals experiencing hair, scalp, and whole-person health
            concerns — with integrity, cultural competence, and a durable evidence base. Every
            KUHAS credential is grounded in supervised clinical practice, protocol authorship
            governance, and a lifelong commitment to continuing education.
          </p>
        </section>

        {/* Values */}
        <section className="mt-12 rounded-2xl border border-purple-100 bg-purple-50/40 p-6 sm:p-8" data-testid="about-values">
          <h2 className="font-display text-2xl text-slate-900">What We Stand For</h2>
          <ul className="mt-4 space-y-3 text-sm text-slate-700 leading-relaxed">
            <li className="flex items-start gap-3">
              <Award className="w-4 h-4 text-purple-900 mt-0.5 shrink-0" />
              <span>
                <strong>Consent-first practice.</strong> Every client relationship begins with
                informed consent and continues under documented protocol governance. AI-drafted
                protocols always require licensed practitioner review before release.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Award className="w-4 h-4 text-purple-900 mt-0.5 shrink-0" />
              <span>
                <strong>Articulated credentials.</strong> Level I → Level II → Level III → DTS
                is a real, cumulative pathway. Each tier unlocks distinct scope of practice —
                referral initiation, protocol authorship, doctoral supervision — enforced in
                the platform, not just on paper.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Award className="w-4 h-4 text-purple-900 mt-0.5 shrink-0" />
              <span>
                <strong>Supervised clinical work.</strong> Residency rotations are matched
                against a shared supervisor pool with atomic capacity claims and per-case
                consent documentation. Nothing about clinical training is aspirational.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <Award className="w-4 h-4 text-purple-900 mt-0.5 shrink-0" />
              <span>
                <strong>Ongoing accountability.</strong> Continuing education is a compliance
                obligation, not a marketing pitch. Practitioners who fall out of CE compliance
                lose protocol-authorship privileges until they return to standing.
              </span>
            </li>
          </ul>
        </section>

        {/* Faculty note */}
        <section className="mt-12" data-testid="about-faculty">
          <h2 className="font-display text-2xl text-slate-900">Founder &amp; Faculty</h2>
          <p className="mt-3 text-base text-slate-700 leading-relaxed">
            KUHAS was founded by <strong>Dr.&nbsp;Christy&nbsp;Vonta</strong>, an educator,
            clinician, and researcher whose work bridges trichology science, herbal medicine,
            and functional wellness. Faculty across the four colleges bring supervised clinical
            experience, published curriculum authorship, and lived commitment to the KISSED
            practice model. Full faculty profiles and appointment listings will be published
            here as onboarding completes.
          </p>
          <p className="mt-4 text-sm text-slate-500 italic">
            Faculty biographies, credentials, and photos are being finalized. Check back for
            complete listings.
          </p>
        </section>

        {/* Colleges */}
        <section className="mt-12" data-testid="about-colleges">
          <h2 className="font-display text-2xl text-slate-900">The Four Colleges of KUHAS</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {COLLEGES.map((c) => {
              const Icon = c.icon;
              return (
                <Link
                  key={c.key}
                  to={c.to}
                  data-testid={`about-college-link-${c.key}`}
                  className="rounded-xl border border-slate-200 bg-white p-4 hover:border-purple-300 hover:shadow-md transition flex items-center gap-3 group"
                >
                  <div className="rounded-lg bg-purple-50 p-2">
                    <Icon className="w-5 h-5 text-purple-900" />
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-slate-900 group-hover:text-purple-900">{c.name}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-purple-900" />
                </Link>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-14 rounded-2xl border border-purple-200 bg-white p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="about-cta">
          <div>
            <h3 className="font-display text-xl text-slate-900">Ready to explore your pathway?</h3>
            <p className="mt-1 text-sm text-slate-600">Five questions. Personalized program recommendation.</p>
          </div>
          <Link to="/university/quiz">
            <Button size="lg" className="bg-purple-900 hover:bg-purple-800 text-white px-6" data-testid="about-cta-quiz">
              Take the Pathway Quiz <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </section>
      </main>
    </div>
  );
}
